import { Fragment, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SectionCard } from '../../../components/design-system/admin/SectionCard';
import type { VendorPlan } from '../our-vendors/models/vendor.model';
import { PLAN_ADDONS, PLAN_GROUPS, addonEnabled, formatPlanPrice, moduleEnabled, priceKind } from './plan-catalog';
import type { PlanModule } from './plan-catalog';
import { usePlanLabels } from './use-plan-labels';
import { PlanIcon } from './PlanIcon';
import { IncludedMark } from './IncludedMark';

interface DetailRow {
  key: string;
  label: string;
  render: (p: VendorPlan) => ReactNode;
}

/** A foldable group's heading row: the whole row toggles it; the button carries the state for keyboards and screen readers. */
function GroupRow({
  testId,
  open,
  onToggle,
  icon,
  title,
  plans,
  cell,
}: {
  testId: string;
  open: boolean;
  onToggle: () => void;
  icon?: ReactNode;
  title: string;
  plans: VendorPlan[];
  cell?: (p: VendorPlan) => ReactNode;
}) {
  return (
    <TableRow data-testid={testId} onClick={onToggle} className="cursor-pointer bg-muted/40 hover:bg-muted/70">
      <TableCell className="py-2.5 ps-6">
        <button
          type="button"
          aria-expanded={open}
          className="flex w-full items-center gap-2.5 rounded-md text-start text-sm font-medium focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
        >
          {icon ?? <span className="size-6 shrink-0" aria-hidden />}
          <span className="flex-1">{title}</span>
          <ChevronDown className={cn('size-4 text-muted-foreground transition-transform', open && 'rotate-180')} aria-hidden />
        </button>
      </TableCell>
      {plans.map((p) => (
        <TableCell key={p._id} className="py-2.5 text-center">
          {cell?.(p)}
        </TableCell>
      ))}
    </TableRow>
  );
}

/**
 * Every plan side by side: price, then one foldable group per module (open by
 * default) with its limits, quality and sub-features, then the add-ons.
 */
export function PlanComparisonTable({ plans }: { plans: VendorPlan[] }) {
  const { t } = useTranslation();
  const labels = usePlanLabels();
  // Folded groups (absent = open).
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const toggleGroup = (id: string) => setCollapsedGroups((p) => ({ ...p, [id]: !p[id] }));

  const detailRows = (m: PlanModule): DetailRow[] => {
    const rows: DetailRow[] = m.limits.map((k) => ({
      key: k,
      label: labels.limitLabel(k),
      render: (p) => labels.limitValue(p.limits?.[k]),
    }));
    if (m.quality) {
      rows.push({
        key: 'quality',
        label: t('PLANS.TABLE.QUALITY'),
        render: (p) => {
          const q = p.features?.visualizationQuality;
          return q ? labels.quality(q) : '—';
        },
      });
    }
    m.extras.forEach((k) =>
      rows.push({ key: k, label: labels.featureLabel(k), render: (p) => <IncludedMark included={Boolean(p.features?.[k])} /> }),
    );
    return rows;
  };

  const priceOf = (p: VendorPlan): string => {
    const kind = priceKind(p);
    if (kind === 'custom') return t('PLANS.PRICE_CUSTOM');
    if (kind === 'free') return t('PLANS.PRICE_FREE');
    if (kind === 'paid') return `${formatPlanPrice(p)}/${t('PLANS.PER_MONTH')}`;
    return '—';
  };

  const addonsOpen = !collapsedGroups.addons;

  return (
    <SectionCard
      title={t('ADMIN_PLANS.COMPARE.TITLE')}
      description={t('ADMIN_PLANS.COMPARE.DESCRIPTION')}
      flush
      testId="admin-plans-comparison-table"
    >
      <Table className="text-sm">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="min-w-56 ps-6 text-start">
              <span className="sr-only">{t('ADMIN_PLANS.COMPARE.FEATURE')}</span>
            </TableHead>
            {plans.map((p) => (
              <TableHead key={p._id} className="min-w-32 px-4 text-center">
                {p.name}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {plans.some((p) => p.price !== undefined) && (
            <TableRow className="hover:bg-transparent">
              <TableCell className="ps-6 text-muted-foreground">{t('PLANS.TABLE.PRICE')}</TableCell>
              {plans.map((p) => (
                <TableCell key={p._id} className="px-4 text-center font-medium tabular-nums">
                  {priceOf(p)}
                </TableCell>
              ))}
            </TableRow>
          )}

          {PLAN_GROUPS.map((m) => {
            const open = !collapsedGroups[m.id];
            return (
              <Fragment key={m.id}>
                <GroupRow
                  testId={`admin-table-group-${m.id}`}
                  open={open}
                  onToggle={() => toggleGroup(m.id)}
                  icon={<PlanIcon Icon={m.Icon} size="sm" />}
                  title={labels.moduleTitle(m)}
                  plans={plans}
                  cell={(p) => <IncludedMark included={moduleEnabled(p.features, m)} />}
                />
                {open &&
                  detailRows(m).map((row) => (
                    <TableRow key={row.key} className="hover:bg-transparent">
                      <TableCell className="ps-[58px] text-muted-foreground">{row.label}</TableCell>
                      {plans.map((p) => (
                        <TableCell key={p._id} className="px-4 text-center tabular-nums">
                          {row.render(p)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </Fragment>
            );
          })}

          <GroupRow
            testId="admin-table-group-addons"
            open={addonsOpen}
            onToggle={() => toggleGroup('addons')}
            title={t('ADMIN_PLANS.ADDONS')}
            plans={plans}
          />
          {addonsOpen &&
            PLAN_ADDONS.map((a) => (
              <TableRow key={a.id} className="hover:bg-transparent">
                <TableCell className="ps-6">
                  <span className="flex items-center gap-2.5">
                    <PlanIcon Icon={a.Icon} size="sm" />
                    <span className="text-muted-foreground">{labels.addonTitle(a)}</span>
                  </span>
                </TableCell>
                {plans.map((p) => (
                  <TableCell key={p._id} className="px-4 text-center">
                    <IncludedMark included={addonEnabled(p.features, a)} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </SectionCard>
  );
}
